import { FDRStatus, FieldFDRStatsResponse, RewardItem } from '@cibo/core'
import { useFigures } from '@cibo/ui'
import { Stack, Typography } from '@mui/material'
import {
  GridColDef,
  GridRenderCellParams,
  GridValueFormatterParams,
  GridValueGetterParams,
} from '@mui/x-data-grid-pro'
import { propEq } from 'ramda'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

type FDRStatusItem = Pick<FieldFDRStatsResponse, 'fdrStats'>

export const useFDRFieldCountColumn = (status: FDRStatus, overrides?: Partial<GridColDef>) => {
  const { t } = useTranslation('@cibo/profile/useFDRColumns')
  const { t: statusT } = useTranslation('FDR_Status')

  const fieldName = `${status}FieldCount`

  return useMemo(
    () => ({
      field: fieldName,
      headerName: t('fieldCount', { status: statusT(status) }),
      valueGetter: (params: GridValueGetterParams<number, FDRStatusItem>) =>
        params.row?.fdrStats?.find(propEq('status', status))?.fields,
      valueFormatter: (params: GridValueFormatterParams<number | undefined>) =>
        !params.value ? '-' : t('fields', { count: params.value }),
      ...overrides,
    }),
    [status]
  )
}

export const useFDRAcresColumn = (status: FDRStatus, overrides?: Partial<GridColDef>) => {
  const { t } = useTranslation('@cibo/profile/useFDRColumns')
  const { t: statusT } = useTranslation('FDR_Status')
  const { acres } = useFigures()

  const fieldName = `${status}Acres`

  return useMemo(
    () => ({
      field: fieldName,
      headerName: t('acresByStatus', { status: statusT(status) }),
      valueGetter: (params: GridValueGetterParams<number, FDRStatusItem>) =>
        params.row?.fdrStats?.find(propEq('status', status))?.acres,
      valueFormatter: (params: GridValueFormatterParams<number>) =>
        !params.value ? '-' : acres(params.value),
      minWidth: 100,
      ...overrides,
    }),
    [status]
  )
}

export const useFDRRewardsColumn = (status: FDRStatus, overrides?: Partial<GridColDef>) => {
  const { t } = useTranslation('@cibo/profile/useFDRColumns')
  const { t: statusT } = useTranslation('FDR_Status')
  const { earnedAmount } = useFigures()

  const fieldName = `${status}Rewards`

  return {
    field: fieldName,
    sortable: false,
    headerName: t('rewards', { status: statusT(status) }),
    valueGetter: (params: GridValueGetterParams<Record<string, RewardItem>>) =>
      params.row?.fdrStats?.find(propEq('status', status))?.rewards?.opportunities,
    renderCell: (p: GridRenderCellParams<Record<string, RewardItem> | undefined>) => {
      if (!p?.value) return
      const rewardItems = Object.values(p.value)
      return (
        <Stack>
          {rewardItems.map(rewardItem => (
            <Typography
              key={rewardItem.token}
              variant={rewardItems.length > 1 ? 'caption' : 'body2'}
            >
              {earnedAmount(rewardItem)}
            </Typography>
          ))}
        </Stack>
      )
    },
    ...overrides,
  }
}
